import * as v from 'valibot'
import type {
  Artwork,
  DetailedTaskParameters,
} from 'src/services/apollo/generated/graphql'
import { getNaturalPrompts } from 'src/services/helpers/artworkParamsExtractor'
import {
  TaskParametersArtFilterSchema,
  TaskParametersGeneratorSchema,
  TaskParametersI2vProSchema,
  TaskParametersReferenceVideoSchema,
  TaskParametersT2i2vSchema,
  TaskParametersWorkflowSchema,
} from 'src/services/helpers/schema/taskParameters'
import {
  isArtFilterTask,
  isI2vProTask,
  isReferenceVideoTask,
  isT2I2VTask,
  isWorkflowTask,
} from 'src/utils/task'

type ArtworkForParser = Pick<
  Artwork,
  | 'authorId'
  | 'task'
  | 'hidePrompts'
  | 'segmentedPrompts'
  | 'prompts'
  | 'extra'
  | 'visibility'
>

/**
 * @example
 * ```json
 * {
 *   "type": "workflow",
 *   "image": {
 *     "type": "media",
 *     "media_id": "627423759050956039"
 *   },
 *   "prompt": "beautiful detailed eyes, portrait, masterpiece, best quality, intricate details, absurdres",
 *   "strength": 0.45,
 *   "workflowName": "kyo/face-detailer:v4.0",
 *   "visibility": "PRIVATE",
 *   "workflowId": "1822750868756425459",
 *   "authorId": "1916973995837468606"
 * }
 * ```
 */
export type ParsedParamsForArtworkDetail = ReturnType<
  typeof parseDisplayableParametersFromTask
>

export function parseDisplayableParametersFromTask(
  taskParameters?: DetailedTaskParameters,
  artwork?: ArtworkForParser,
) {
  if (!taskParameters) return undefined
  const isI2vPro = isI2vProTask(taskParameters)
  const isReferenceVideo = isReferenceVideoTask(taskParameters)
  const isT2I2v = isT2I2VTask(taskParameters)
  const isWorkflow = isWorkflowTask(taskParameters)
  const isArtFilter = isArtFilterTask(taskParameters)

  if (isArtFilter) {
    const { output, success } = v.safeParse(
      TaskParametersArtFilterSchema,
      taskParameters.parameters,
    )
    if (success)
      return {
        type: 'art-filter',
        filterId: output.inputs.filterId,
        strength: output.inputs.strength,
        mediaId: output.mediaId,
        // Artwork Parameters
        authorId: artwork?.authorId,
      } as const
  } else if (isI2vPro) {
    const { output, success, issues } = v.safeParse(
      TaskParametersI2vProSchema,
      taskParameters.parameters,
    )
    // console.log({ issues })
    if (success)
      return {
        type: 'i2vPro',
        ...output.i2vPro,
        sceneId: output.sceneId ?? artwork?.task?.parameters?.sceneId,
        // Artwork Parameters
        authorId: artwork?.authorId,
        hidePrompts: artwork?.hidePrompts,
        segmentedPrompts: artwork?.segmentedPrompts,
      } as const
  } else if (isReferenceVideo) {
    const { output, success } = v.safeParse(
      TaskParametersReferenceVideoSchema,
      taskParameters.parameters,
    )
    if (success)
      return {
        type: 'ref2v',
        model: output.referenceVideo.model,
        mode: output.referenceVideo.mode,
        prompts: output.referenceVideo.prompt,
        duration:
          output.referenceVideo.duration == null
            ? undefined
            : String(output.referenceVideo.duration),
        mediaId:
          output.referenceVideo.referenceImageMediaIds?.[0] ??
          output.referenceVideo.referenceVideoMediaIds?.[0],
        generateAudio: output.referenceVideo.generateAudio,
        referenceImageMediaIds:
          output.referenceVideo.referenceImageMediaIds ?? [],
        referenceVideoMediaIds:
          output.referenceVideo.referenceVideoMediaIds ?? [],
        referenceAudioMediaIds:
          output.referenceVideo.referenceAudioMediaIds ?? [],
        sceneId: output.sceneId ?? artwork?.task?.parameters?.sceneId,
        // Artwork Parameters
        authorId: artwork?.authorId,
        hidePrompts: artwork?.hidePrompts,
        segmentedPrompts: artwork?.segmentedPrompts,
      } as const
  } else if (isT2I2v) {
    const { success, output, issues } = v.safeParse(
      TaskParametersT2i2vSchema,
      taskParameters.parameters,
    )
    // console.log({ issues })
    if (success) {
      return {
        type: 't2i2v',
        ...output,
        model: output.t2i2v.model,
        // Artwork Parameters
        prompts: artwork?.prompts ?? output.prompts ?? undefined,
        segmentedPrompts: artwork?.segmentedPrompts,
        hidePrompts: artwork?.hidePrompts,
        magicPrompts: artwork?.extra?.naturalPrompts,
        authorId: artwork?.authorId,
      } as const
    }
  } else if (isWorkflow) {
    const { output, success, issues } = v.safeParse(
      TaskParametersWorkflowSchema,
      taskParameters.parameters,
    )
    // console.log({ issues })
    if (success)
      return {
        type: 'workflow',
        ...output.inputs,
        workflowName: output.workflowName,
        // Artwork Parameters
        visibility: artwork?.visibility,
        workflowId: artwork?.extra.workflowId,
        authorId: artwork?.authorId,
      } as const
  } else {
    const { success, output, issues } = v.safeParse(
      TaskParametersGeneratorSchema,
      taskParameters.parameters,
    )

    // console.log({ issues })
    if (success)
      return {
        /* Normal Generator Task */
        type: 'generator',
        ...output,
        sceneId: output.sceneId ?? artwork?.task?.parameters?.sceneId,
        // Artwork Parameters
        prompts: artwork?.prompts ?? output.prompts ?? undefined,
        segmentedPrompts: artwork?.segmentedPrompts,
        hidePrompts: artwork?.hidePrompts,
        magicPrompts: getNaturalPrompts(artwork, taskParameters),
        authorId: artwork?.authorId,
        modelStyle: output.modelStyle,
        watermark: output.watermark,
      } as const
  }
}
